import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api/client';
import { formatMkd } from '../lib/format';

/**
 * Devijimi i rrogave — planned versus actual salary spend, by month.
 * Ports frmRogatDevijimi. Positive means under budget.
 */

interface DevijimiMonth {
  year: number;
  month: number;
  monthName: string;
  planned: number;
  actual: number;
  deviation: number;
}

interface Response {
  summary: { planned: number; actual: number; deviation: number };
  months: DevijimiMonth[];
}

function formatDeviation(value: number): string {
  return `${value >= 0 ? '+' : '−'}${formatMkd(Math.abs(value))}`;
}

function deviationClass(value: number): string {
  return value >= 0 ? 'text-emerald-700' : 'text-red-600';
}

export default function RrogatDevijimi() {
  const dataQuery = useQuery({
    queryKey: ['rrogat-devijimi'],
    queryFn: () => apiGet<Response>('/rrogat/devijimi'),
  });

  const summary = dataQuery.data?.summary;
  const months = dataQuery.data?.months ?? [];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">Devijimi i rrogave</h1>
        <p className="text-sm text-muted">
          Planifikuar kundrejt aktuales. Vlera pozitive do të thotë nën buxhet.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl bg-white p-4">
          <div className="text-xs font-medium text-muted">Rrogat aktuale (ky muaj)</div>
          <div className="tnum mt-1 text-xl font-semibold">{formatMkd(summary?.actual ?? 0)}</div>
        </div>
        <div className="rounded-xl bg-white p-4">
          <div className="text-xs font-medium text-muted">Të planifikuara (ky muaj)</div>
          <div className="tnum mt-1 text-xl font-semibold">{formatMkd(summary?.planned ?? 0)}</div>
        </div>
        <div className="rounded-xl bg-white p-4">
          <div className="text-xs font-medium text-muted">Devijimi</div>
          <div className={`tnum mt-1 text-xl font-semibold ${deviationClass(summary?.deviation ?? 0)}`}>
            {formatDeviation(summary?.deviation ?? 0)}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-4 py-3 font-semibold">Muaji</th>
                <th className="px-4 py-3 text-right font-semibold">Planifikuar</th>
                <th className="px-4 py-3 text-right font-semibold">Aktuale</th>
                <th className="px-4 py-3 text-right font-semibold">Devijimi</th>
              </tr>
            </thead>
            <tbody>
              {dataQuery.isLoading && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-muted">
                    Duke ngarkuar…
                  </td>
                </tr>
              )}

              {!dataQuery.isLoading && months.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-muted">
                    Nuk ka të dhëna.
                  </td>
                </tr>
              )}

              {months.map((m) => (
                <tr key={`${m.year}-${m.month}`} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-2">
                    {m.monthName} {m.year}
                  </td>
                  <td className="tnum px-4 py-2 text-right">{formatMkd(m.planned)}</td>
                  <td className="tnum px-4 py-2 text-right">{formatMkd(m.actual)}</td>
                  <td className={`tnum px-4 py-2 text-right font-medium ${deviationClass(m.deviation)}`}>
                    {formatDeviation(m.deviation)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
